import React, {useState, useEffect, useContext, useRef} from 'react'
import Grid from '@mui/material/Grid';
import { Button, Typography, Card } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import {RTCVideo} from '@dmc/react-rtc-media-components';
import ConfigContext from '../contexts/configContext'
import DefaultPoster from '../images/default-video-poster.png'
import useWindowSize from '../hooks/useWindowSize';
import Drawer from '@mui/material/Drawer';
import { IconButton, Tooltip } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PushPinIcon from '@mui/icons-material/PushPin';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Participants from './Participants';
import DTMF from './DTMF';
import Settings from './Settings';
import DTMFActionButtons from './DTMFActionButtons';
import Chat from './Chat';
import { chunk } from '../helpers/ArrayHelpers';
import { borderRadius } from '@mui/system';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function VideoRoom(
  {
    streams,
    confbridgeChannels,
    confbridgeChannelAvatars,
    confbridgeTalkingStatus, 
    confbridgeAdmin, 
    confbridgeModerator, 
    msidChannelIdMap, 
    dtmfActions,
    drawerContent, 
    setDrawerContent, 
    notifications, 
    screensharing,
    chatMessages,
    cameraAllowed,
    audioDevices,
    videoDevices,
    selectedAudioDeviceId,
    selectedVideoDeviceId,
    localVideoStream,
    onNotificationDelete, 
    onNudgeSend,
    onKickSend,
    onDtmfKey,
    onAudioChange,
    onVideoChange,
    onSendChatMessage,
  }) {
  const [columns, setColumns] = useState(1)
  const [maxRowHeight, setMaxRowHeight] = useState(0)
  const [maxVideoWidth, setMaxVideoWidth] = useState(0)
  const [pinnedStreamId, setPinnedStreamId] = useState(null)
  const [confbridgeChannelOptsOpen, setConfbridgeChannelOptsOpen] = useState(new Map())
  const [drawerOpen, setDrawerOpen] = useState(false)
  const windowSize = useWindowSize()
  const config = useContext(ConfigContext)
  const roomRef = useRef()

  useEffect(() => {
    if (drawerContent) {
      setDrawerOpen(true)
    } else {
      setDrawerOpen(false)
    }
  }, [drawerContent])

  useEffect(() => {
    if (!streams) return
    const count = streams.length
    var requiredColumns = 1
    if (windowSize.portrait) {
      if (count <= 2) {
          requiredColumns = 1
      } else if (count <= 4) {
          requiredColumns = 2
      } else if (count <= 6) {
          requiredColumns = 3
      } else if (count <= 8) {
          requiredColumns = 4
      } else if (count > 8) {
          requiredColumns = 5
      }
    } else {
      if (count <= 1) {
          requiredColumns = 1
      } else if (count <= 4) {
          requiredColumns = 2
      } else if (count <= 6) {
          requiredColumns = 3
      } else if (count <= 8) {
          requiredColumns = 4
      } else if (count > 8) {
          requiredColumns = 5
      }
    }
    const rows = Math.ceil(count / requiredColumns)
    setMaxRowHeight(windowSize.height / rows)
    setMaxVideoWidth(windowSize.width / requiredColumns)
    setColumns(requiredColumns)
  }, [streams, windowSize])

  useEffect(() => console.log('maxRowHeight changed', maxRowHeight), [maxRowHeight])

  const renderStream = (stream, i, units) => {
    let h = pinnedStreamId ? windowSize.height : maxRowHeight
    let msid = stream.getVideoTracks()[0].id
    let channelId = msidChannelIdMap.get(msid)
    let confbridgeChannel
    let optsOpen
    let talking

    if (confbridgeChannels) {
      confbridgeChannel = confbridgeChannels.find(channel => channel.id === channelId)
      optsOpen = confbridgeChannelOptsOpen.get(confbridgeChannel?.id)
      talking = confbridgeTalkingStatus.get(channelId)
      if (confbridgeChannel?.caller?.name === "MODERATOR") return // do not render moderator streams
    }

    const vAR = stream.getVideoTracks()[0].getSettings().aspectRatio
    const vPortrait = vAR < 1 ? true: false
    const maxWidth = h * vAR

    return ( 
        <Grid
            key={i}
            item
            // xs={units}
            xs
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
        >
            {/* blurred background effect */}
            {/* <RTCVideo
              srcObject={stream}
              style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  objectFit: "cover",
                  top: 0,
                  left: 0,
                  opacity: '0.7',
                  filter: 'blur(100px)'
              }}
              autoPlay
              muted
            /> */}
            <div 
              style={{
                height: h,
                display: 'flex',
                overflow: 'hidden',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flex: 'auto'
              }}
            >
              <div
                style={{
                  position: 'relative',
                  maxHeight: 'calc(100%)',
                  maxWidth: maxWidth,
                  height: vPortrait ? 'calc(100% )' : undefined,
                  width: !vPortrait ? 'calc(100% )' : undefined,
                  display: 'flex',
                  flexGrow: "1",
                  overflow: 'hidden'
                }}
              >

                <RTCVideo
                  srcObject={stream}
                  key={i}
                  id={stream.id}
                  style={{
                      width: !vPortrait ? '100%' : undefined,
                      height: vPortrait ? '100%' : undefined,
                      objectFit: "cover",
                      // maxHeight: '100%',
                      // maxWidth: '100%',
                  }}
                  autoPlay
                  muted
                  onDoubleClick={() => {
                    if (streams.length > 1) setPinnedStreamId(stream.id)
                  }}
                />
                {optsOpen ? renderChannelOpts(confbridgeChannel, stream.id) : null}
                {confbridgeChannel ?  renderParticipantInfo(confbridgeChannel): null}
                {talking ? renderTalkingBlob() : null}
              </div>
            </div>
        </Grid>
    )

  }

  const renderRows = () => {
    const rows = chunk([...streams], columns)
    return rows.map((row, i) => renderRow(row, i))
  }

  const renderRow = (row, i) => {
    return (
      <Grid
        key={i}
        container
        style={{
          height: maxRowHeight,
          width: "100%",
          justifyContent: "center",
        }}
      >
        {row.map((stream, i) => renderStream(stream, i))}
      </Grid>
    )
  }

  const renderParticipantInfo = (channel) => {
    return (
      <div className='live-call-call-remote-video-conf-info'>
        <Typography variant='body2'>{channel.caller.name}</Typography>
        <Tooltip title="Options" arrow>
          <IconButton
            onClick={() => {
              let newstate = !confbridgeChannelOptsOpen.get(channel.id)
              setConfbridgeChannelOptsOpen(new Map(confbridgeChannelOptsOpen.set(channel.id, newstate)))
            }}
          >
            <MoreVertIcon fontSize='small'/>
          </IconButton>
        </Tooltip>
        {/* {renderNudgeButton(channel)} */}
      </div>
    ) 
  }
  
  const renderChannelOpts = (channel, streamId) => {
    return (
      <div 
        className='live-call-call-remote-video-conf-opts'
        onClick={() => setConfbridgeChannelOptsOpen(new Map(confbridgeChannelOptsOpen.set(channel.id, false)))}
      >
        <div className='live-call-call-remote-video-conf-opts-buttons'>
          {streams.length > 1 ? renderPinButton(channel, streamId) : null}
          {channel ? renderNudgeButton(channel) : null}
          {channel && confbridgeAdmin ? renderKickButton(channel) : null}
        </div>
      </div>
    )
  }

  const renderTalkingBlob = () => {
    return (
      <div className='live-call-call-remote-video-conf-talking-blob'/>
    )
  }

  const renderNudgeButton = (channel) => {
    return (
      <Button
        fullWidth
        style={{justifyContent: "flex-start"}}
        variant="contained"
        startIcon={<NotificationImportantIcon />}
        onClick={() => {
          onNudgeSend(channel.id)
        }}
      >
        Nudge
      </Button>
    )
  }

  const renderKickButton = (channel) => {
    return (
      <Button
        fullWidth
        color="danger"
        style={{justifyContent: "flex-start"}}
        variant="contained"
        startIcon={<RemoveCircleIcon />}
        onClick={() => {
          onKickSend(channel.id)
        }}
      >
        Kick
      </Button>
    )
  }

  const renderPinButton = (channel, streamId) => {
    return (
      <Button
        fullWidth
        style={{justifyContent: "flex-start"}}
        variant="contained"
        startIcon={<PushPinIcon />}
        onClick={() => {
          // setConfbridgeChannelOptsOpen(new Map(confbridgeChannelOptsOpen.set(channel.id, false)))
          setPinnedStreamId(streamId)
        }}
      >
        Pin Video
      </Button>
    )
  }

  const renderBackToGrid = () => {
    return (
      <div className='live-call-video-room-back'>
        <Button
          variant="contained"
          startIcon={<GridViewIcon />}
          onClick={() => setPinnedStreamId(null)}
        >
          Grid
        </Button>
      </div>
    )
  }

  const renderPinnedStream = () => {
    let components = [];
    let stream = streams.find(s => s.id === pinnedStreamId);
    if (stream) {
      components.push(renderStream(stream, stream.id, 12))
      components.push(renderBackToGrid())
    } else {
      setPinnedStreamId(null)
    }
    return components
  }

  const renderNotifications = () => {
    let components = []
    for (const [key, value] of notifications.entries()) {
      // components.push(<div key={key}>{value}</div>)
      components.push(
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          key={key}
          open
          autoHideDuration={value.sticky ? undefined : 6000}
          onClose={() => onNotificationDelete(key)}
        >
          <Alert 
            onClose={() => onNotificationDelete(key)}
            severity={value.severity} 
            sx={{ width: '100%', boxShadow: 0 }}
            action={value.action ? value.action : null}
          >
            {value.msg}
          </Alert>
        </Snackbar>
      )
    }
    return components
  }

  const renderDrawer = () => {
    return (
      <div className='live-call-video-room-sidebar'>
        <div className='live-call-video-room-sidebar-top'>
          <IconButton
            onClick={() => {
              setDrawerContent(null)
            }}
          >
            <ChevronLeftIcon/>
          </IconButton>
          <h2>{drawerContent}</h2>
        </div>
        {renderDrawerContent()}
      </div>
    )
  }

  const renderDrawerContent = () => {
    let content
    switch (drawerContent) {
      case 'Keypad':
        content = (
          <DTMF
            onKeyPress={onDtmfKey}
          />
        )
        break;
      case 'Settings':
        content = (
            <Settings
            container={roomRef.current}
            localVideoStream={localVideoStream}
            screensharing={screensharing}
            videoEnabled={cameraAllowed}
            audioDevices={audioDevices}
            videoDevices={videoDevices}
            audioDeviceId={selectedAudioDeviceId}
            videoDeviceId={selectedVideoDeviceId}
            onAudioChange={onAudioChange}
            onVideoChange={onVideoChange}
          />
        )
        break;
      case 'Actions':
        content = (
          <DTMFActionButtons
            actions={dtmfActions}
            onClick={(tone) => onDtmfKey(tone)}
          />
        )
        break;
      case 'Participants':
        content = (
          <Participants
            confbridgeChannels={confbridgeChannels}
            confbridgeChannelAvatars={confbridgeChannelAvatars}
            confbridgeTalkingStatus={confbridgeTalkingStatus}
          />
        )
        break;
      case 'Conversation':
        content = (
          <Chat 
            confbridgeChannels={confbridgeChannels}
            confbridgeChannelAvatars={confbridgeChannelAvatars}
            confbridgeModerator={confbridgeModerator}
            chatMessages={chatMessages}
            onSendChatMessage={onSendChatMessage}
          />
        )
        break;
      default:
        break;
    }
    return (
      <div className='live-call-video-room-sidebar-content'>
        {content}
      </div>
    )
  }

  let bg = config?.devices?.posterUrl ?  config.devices.posterUrl :  `${DefaultPoster}`
  if (streams.length > 0) bg = null

  return (
    <div 
      ref={roomRef}
      className='live-call-video-room'
      style={{
        backgroundImage: bg ? `url(${bg})` : 'none',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        backgroundPosition: 'center'
      }}
    >
      {renderNotifications()}
      <div className='live-call-video-room-container'>
        <Drawer
          anchor="left"
          variant="persistent"
          open={drawerOpen}
          onClose={() => setDrawerContent(null)}
        >
          {renderDrawer()}
        </Drawer>
        <Grid
          id="remote-videos"
          container
          // spacing={8}
          style={{
            height: "100%",
            width: "100%",
            justifyContent: "center",
          }}
        >
        {pinnedStreamId && streams.length > 1 ?
          renderPinnedStream()
        :
          renderRows()
        }
        </Grid>

      </div>
    </div>
  )
}
